<script lang="ts">
	import { createEventDispatcher } from 'svelte'

	import CheckIcon from '$lib/components/Common/Icons/CheckIcon.svelte'
	import { capitalizeFirst } from '$lib/functions/formatters'
    import type { SubscriptionTableInfo } from '$lib/stores/subscription_table_info/types'
	import type { SubscriptionFeature, SubscriptionPlanFeature } from '$lib/types/subscription_types'

	export let currency_text: string = ''
	export let subscriptionPlanFeatures: SubscriptionPlanFeature[] = []
	export let subscriptionFeatures: SubscriptionFeature[] = []
	export let data: SubscriptionTableInfo
	// export let applyCoupon = true
	export let discountOn = true
	export let disabled = false
	export let buyText = 'Buy Now'

	let borderClass = ''
	$: textData = getTextData(data, discountOn)
	$: mySubscriptionPlanFeatures = getSubscriptionPlanFeaturesFiltered(
		subscriptionPlanFeatures,
		data.sub_plan_key
	)
	$: mySubscriptionFeatures = getSubscriptionFeaturesFiltered(subscriptionFeatures)

	$: {
		if (textData.top_label) borderClass = 'border-gray-600'
		else borderClass = 'border-gray-200'
	}

	const dispatch = createEventDispatcher()

	function getSubscriptionPlanFeaturesFiltered(
		response: SubscriptionPlanFeature[],
		plan_key: string
	) {
		// obtain subscriptionPlanFeatures of plan
		var subsPlanFeatures = response.filter((f) => f.plan_key == plan_key && f.enabled && f.visible)

		return subsPlanFeatures
	}
	function getSubscriptionFeaturesFiltered(response: SubscriptionFeature[]) {
		// obtain subscriptionFeatures of plan
		var subsPlanFeatures = response

		return subsPlanFeatures
	}

	const getFeatureStyle = (planFeature: SubscriptionPlanFeature) => {
		let featureStyle = ''
		if (planFeature.feature_format) {
			if (planFeature.feature_format == 'bold') featureStyle += 'font-weight: bold; '
		}
		if (planFeature.feature_color) {
			featureStyle += 'color: ' + planFeature.feature_color
		}
		//
		return featureStyle
	}

	const getFeatureName = (featureID: string) => {
		let feature = mySubscriptionFeatures.find((feature) => feature.id === featureID)
		if (feature) return feature.name_visible
		//
		return 'unknow feature'
	}

	async function subscribe() {
		if (disabled) return

		const result = {
			subscription_period: data.recurring_interval,
			price_id: data.price_id,
			coupon_id: discountOn ? data.stripe_coupon_id : '',
			sub_plan_key: data.sub_plan_key
		}
		dispatch('subscribe', result)
	}

	function getTextData(data: SubscriptionTableInfo, tougleDiscountOn: boolean) {
		// if product not have coupon then return default values
		//// if (tougleDiscountOn) {
		if (tougleDiscountOn && data.stripe_coupon_id) {
			return {
				top_label: data.coupon_top_label,
				top_label_bgcolor: data.coupon_top_label_bgcolor,
				// text_linethrough : data.coupon_text_linethrough,
				text_linethrough: data.default_text_primary,
				text_primary: data.coupon_text_primary,
				text_period: data.coupon_text_period,
				// text_primary_comment : 'my coupon',
				text_comment: data.coupon_text_comment
			}
		} else {
			return {
				top_label: data.default_top_label,
				top_label_bgcolor: data.default_top_label_bgcolor,
				// text_linethrough : data.default_text_linethrough,
				text_linethrough: '',
				text_primary: data.default_text_primary,
				text_period: data.default_text_period,
				// text_primary_comment : 'my default',
				text_comment: data.default_text_comment
			}
		}
	}

	function getCapitalLabel(value: string) {
		// value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
		value = capitalizeFirst(value)
		return value
	}

	const getTopLabelStyle = (bgcolor: string) => {
		let featureStyle = ''
		if (bgcolor) {
			featureStyle += 'background-color: ' + bgcolor
		}
		//
		return featureStyle
	}
</script>

<div class="relative border rounded-lg shadow-sm divide-y divide-gray-200 bg-white {borderClass}">
	<div class="p-6">
		{#if textData.top_label}
			<div class="grid place-items-center">
				<div
					data-test-id="subscribe_top-label"
					class="absolute top-0 p-2 bg-gray-600 rounded text-sm font-semibold text-white transform -translate-y-1/2"
					style={getTopLabelStyle(textData.top_label_bgcolor)}
				>
					{textData.top_label}
				</div>
			</div>
		{/if}

		<h2 class="text-3xl leading-8 font-medium text-gray-900 text-center">
			{getCapitalLabel(data.sub_type_name_visible)}
		</h2>
		{#if discountOn}
			<p class="mt-4 text-base font-semibold text-gray-500 line-through text-center">
				{textData.text_linethrough}
			</p>
		{/if}
		<p class="my-2 text-center whitespace-nowrap">
			{#if currency_text}
				<span class="text-2xl font-abold text-gray-700">{currency_text}</span>
			{/if}
			<span class="text-4xl font-extrabold text-gray-900">{textData.text_primary}</span>
			{#if textData.text_period}
				<span class="text-2xl font-abold text-gray-700">{textData.text_period}</span>
			{/if}
		</p>
		<p class="my-2 py-2 text-center">
			<span class="text-base font-medium text-gray-700">{textData.text_comment}</span>
		</p>

		<div
			class="text-center
					flex items-center justify-center w-full"
		>
			<!-- The buy button should always be shown -->
			<!-- {#if (applyCoupon && discountOn) || !applyCoupon} -->
			{#if true}
				<a
					href={''}
					data-test-id="subscribe_buy_now"
					class="rounded-md py-2 my-4 min-w-[150px] w-full
							text-sm font-semibold text-white text-center
							{disabled ? 'button-action-discount-disabled' : 'button-action-discount'} "
					on:click|preventDefault|stopPropagation={() => subscribe()}
					aria-describedby="tier-standard"
					>{buyText}
				</a>
			{:else}
				<span
					class="rounded-md py-2 my-4 min-w-[150px] w-full
							text-sm font-semibold text-gray-600 text-center
							bg-gray-200"
					aria-describedby="tier-standard"
					>After free trial
				</span>
			{/if}
		</div>

		<div class="specsBlock">
			<div class="rowSpecs">
				<ul class="mt-6 space-y-4 w-1/2">
					<!-- {#each mySubscriptionPlanFeatures as planFeature, i (planFeature.id)} -->
					{#each mySubscriptionPlanFeatures as planFeature (planFeature.id)}
						<li class="flex space-x-3 min-w-[180px] md:min-w-[200px]">
							<CheckIcon />
							<span class="text-sm text-gray-500" style={getFeatureStyle(planFeature)}>
								{getFeatureName(planFeature.feature_id)}
							</span>
						</li>
					{/each}
				</ul>
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	.button-action-discount {
		@apply bg-emerald-500 text-white;
	}
	.button-action-discount:hover {
		@apply bg-emerald-400;
	}
	.button-action-discount-disabled {
		@apply bg-gray-200 text-white cursor-default pointer-events-none;
	}

	.specsBlock {
		@apply inline-block;
	}
	.rowSpecs {
		text-align: left;
		margin-bottom: 1px;
	}
</style>
